import axios from "axios";

// A clinic can have a very large catalogue, and a full stock balance has one row
// per product (per branch for HQ). One response for all of it would hit the
// Lambda/API Gateway payload limit, so the API serves it in slices
// (?limit=&offset=) and the file is stitched together here.
//
// Slices after the first are fetched a few at a time: that spreads the work
// across Lambda instances instead of one long request, without opening a
// request per slice for a very large export.
export const CHUNK_ROWS = 2000;
// The movement report is sliced by product blocks (one product in one branch,
// each with all its ledger rows of the period), not by rows, so a slice carries
// many more lines than a balance slice does — hence a smaller slice.
export const MOVEMENT_CHUNK_BLOCKS = 200;
export const CHUNK_CONCURRENCY = 3;
const ATTEMPTS = 2; // one retry for a transient network/gateway blip

const isChunk = data =>
  data &&
  typeof data.header === "string" &&
  typeof data.content === "string" &&
  Number.isInteger(data.total);

async function fetchChunk(url, params, offset, asOf, chunkRows) {
  let lastError;
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    try {
      // Accept is left at axios' default on purpose: that is what lets the API
      // gzip a large JSON body (see compressJsonResponse middleware).
      const res = await axios.get(url, {
        params: {
          ...params,
          format: "json",
          limit: chunkRows,
          offset,
          ...(asOf ? { asOf } : {}),
        },
      });
      return res.data;
    } catch (err) {
      lastError = err;
      // a 4xx will not get better by asking again
      const status = err?.response?.status;
      if (status && status < 500 && status !== 429) break;
    }
  }
  throw lastError;
}

// Runs `worker` over `items` with at most `limit` in flight, keeping the result
// order the same as the input order. Stops on the first failure.
async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await worker(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return results;
}

const toBlob = text =>
  new Blob(["﻿", text], { type: "text/csv;charset=utf-8" });

/**
 * Download a chunked CSV report and return it as one Blob.
 *
 * @param {string} url
 * @param {Object} params - the report's own filters (date, branchIds, ...)
 * @param {(fraction: number) => void} [onProgress] - 0..1 of the report fetched
 * @param {{ chunkRows?: number }} [options] - slice size (default CHUNK_ROWS)
 * @returns {Promise<Blob>}
 */
export async function downloadChunkedCsv(url, params, onProgress, { chunkRows = CHUNK_ROWS } = {}) {
  const first = await fetchChunk(url, params, 0, undefined, chunkRows);

  // An API that predates chunking answers the whole report as { fileName,
  // content }: use it as is, so a web deploy that lands first still works.
  if (!isChunk(first)) {
    if (typeof first?.content !== "string") {
      throw new Error("Unexpected stock report response: missing CSV content");
    }
    onProgress?.(1);
    return toBlob(first.content);
  }

  const chunks = [first];
  const offsets = [];
  for (let o = first.nextOffset; o !== null && o !== undefined && o < first.total; o += chunkRows) {
    offsets.push(o);
  }

  let fetchedRows = chunkRows; // the first slice (clamped to total below)
  const report = () =>
    onProgress?.(first.total > 0 ? Math.min(1, fetchedRows / first.total) : 1);
  report();

  const rest = await mapWithConcurrency(offsets, CHUNK_CONCURRENCY, async offset => {
    // every slice is computed at the same instant as the first one
    const chunk = await fetchChunk(url, params, offset, first.asOf, chunkRows);
    if (!isChunk(chunk)) {
      throw new Error("Unexpected stock report response: missing CSV content");
    }
    fetchedRows += chunkRows;
    report();
    return chunk;
  });
  chunks.push(...rest);

  const lines = [first.header];
  for (const chunk of chunks) {
    if (chunk.content) lines.push(chunk.content);
  }
  onProgress?.(1);
  return toBlob(lines.join("\n"));
}
